// =============================================================================
// VandieZ admin-web -- Load detail (/loads/:loadId)
// =============================================================================
// Single payload from GET /admin/loads/{id} renders the lane + shipper
// + bids (up to 50) + every trip spawned from this load. Operators use
// this when a dispute or fraud signal points back to a specific load.
// =============================================================================

import { AlertCircle, ArrowLeft, Ban, MapPin, Package, Truck } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { loadsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { useToast } from "@/lib/toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AdminLoadDetail } from "@/types/admin_load";

export function LoadDetailPage(): React.JSX.Element {
  const { loadId } = useParams<{ loadId: string }>();
  const { accessToken } = useAuth();
  const toast = useToast();
  const [load, setLoad] = useState<AdminLoadDetail | null>(null);
  const [audit, setAudit] = useState<
    | {
        entries: Array<{
          changed_at: string;
          actor_user_id: string | null;
          old_status: string | null;
          new_status: string;
          notes: string | null;
          change_source: string | null;
        }>;
      }
    | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expireOpen, setExpireOpen] = useState(false);
  const [expireReason, setExpireReason] = useState("");
  const [expireSubmitting, setExpireSubmitting] = useState(false);

  const fetchLoad = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!accessToken || !loadId) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await loadsAdminApi.detail(accessToken, loadId, signal);
        setLoad(res);
        void loadsAdminApi
          .audit(accessToken, loadId, signal)
          .then(setAudit)
          .catch(() => { /* card hidden on error */ });
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError ? e.problem.detail : "Couldn't load.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, loadId],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchLoad(c.signal);
    return () => { c.abort(); };
  }, [fetchLoad]);

  return (
    <div className="space-y-4">
      <PageHeader
        title={load !== null ? `Load · ${load.pickup_district} → ${load.drop_district}` : "Load detail"}
        description="Admin triage view: shipper, bids (capped 50), spawned trips."
        actions={
          <div className="flex gap-2">
            {load !== null && load.status === "open_for_bidding" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setExpireOpen(true); }}
              >
                <Ban className="mr-1 h-4 w-4" />
                Force-expire
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link to="/loads">
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Link>
            </Button>
          </div>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && load === null ? (
        <Skeleton className="h-40 w-full" />
      ) : load === null ? null : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-baseline justify-between gap-2">
                <span>
                  {load.pickup_district} → {load.drop_district}
                </span>
                <span
                  className={cn(
                    "rounded-full px-3 py-0.5 text-xs uppercase tracking-wide font-medium",
                    statusClass(load.status),
                  )}
                >
                  {load.status.replace(/_/g, " ")}
                </span>
              </CardTitle>
              <CardDescription>
                {load.material_category} · {load.weight_kg} kg ·{" "}
                {load.required_vehicle_type} · {load.estimated_distance_km} km
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Pickup
                </div>
                <div className="text-sm flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <span>{load.pickup_address}</span>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Drop
                </div>
                <div className="text-sm flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <span>{load.drop_address}</span>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Shipper
                </div>
                <Link
                  to={`/users/${load.shipper_id}`}
                  className="text-sm font-medium hover:underline"
                >
                  {load.shipper_name ?? load.shipper_id.slice(0, 8)}
                  {load.shipper_phone !== null && (
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {load.shipper_phone}
                    </span>
                  )}
                </Link>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Budget range
                </div>
                <div className="text-sm font-mono">
                  ₹{Number(load.budget_min).toLocaleString("en-IN")} – ₹
                  {Number(load.budget_max).toLocaleString("en-IN")}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Bid window ends
                </div>
                <div className="text-sm">
                  {new Date(load.bid_window_ends_at).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Created
                </div>
                <div className="text-sm">
                  {new Date(load.created_at).toLocaleString()}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-4 w-4" /> Bids ({load.bids.length})
              </CardTitle>
              <CardDescription>
                Up to 50 most recent bids on this load.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {load.bids.length === 0 ? (
                <div className="py-4 text-sm text-muted-foreground">
                  No bids yet.
                </div>
              ) : (
                <ul className="divide-y text-sm">
                  {load.bids.map((b) => (
                    <li
                      key={b.bid_id}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/users/${b.bidder_id}`}
                          className="font-medium hover:underline"
                        >
                          {b.bidder_name ?? b.bidder_id.slice(0, 8)}
                        </Link>
                        <span className="ml-2 text-xs text-muted-foreground capitalize">
                          ({b.bidder_role})
                        </span>
                        <div className="text-xs text-muted-foreground">
                          {new Date(b.created_at).toLocaleString()}
                        </div>
                      </div>
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          b.status === "accepted"
                            ? "bg-success-subtle text-success-subtle-foreground"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        {b.status}
                      </span>
                      <div className="font-mono tabular-nums shrink-0">
                        ₹{Number(b.amount).toLocaleString("en-IN")}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-4 w-4" /> Trips ({load.trips.length})
              </CardTitle>
              <CardDescription>
                Every trip spawned from this load (first driver + any
                fallbacks if the first bailed).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {load.trips.length === 0 ? (
                <div className="py-4 text-sm text-muted-foreground">
                  No trips yet.
                </div>
              ) : (
                <ul className="divide-y text-sm">
                  {load.trips.map((t) => (
                    <li key={t.trip_id} className="py-2">
                      <Link
                        to={`/trips/${t.trip_id}`}
                        className="flex items-center justify-between gap-3 hover:bg-muted/40 rounded px-2 py-1 -mx-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-xs text-muted-foreground">
                            {t.trip_id.slice(0, 8)}
                          </div>
                          <div className="font-medium">
                            {t.driver_name ?? t.driver_id.slice(0, 8)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(t.created_at).toLocaleString()}
                          </div>
                        </div>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                          {t.status.replace(/_/g, " ")}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {audit !== null && audit.entries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  Status history ({audit.entries.length})
                </CardTitle>
                <CardDescription>
                  Every transition logged in audit.load_status_changes
                  (Postgres trigger). Oldest first.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {audit.entries.map((e, i) => (
                    <li
                      key={`${e.changed_at}-${i}`}
                      className="flex items-start gap-2"
                    >
                      <span className="font-mono text-xs rounded bg-muted px-1.5 py-0.5 shrink-0">
                        {e.old_status ?? "∅"} → {e.new_status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(e.changed_at).toLocaleString()}
                        {e.change_source !== null && (
                          <> · {e.change_source}</>
                        )}
                        {e.notes !== null && <> · {e.notes}</>}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {expireOpen && load !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Force-expire load</CardTitle>
              <CardDescription>
                Sets load to status=expired and closes the bid window.
                Use only when the shipper has vanished and the load is
                stuck. Reason ≥ 10 chars required (audit trail).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={expireReason}
                onChange={(e) => { setExpireReason(e.target.value); }}
                placeholder="e.g. Shipper unreachable for 48h; multiple support attempts ignored."
                rows={4}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setExpireOpen(false);
                    setExpireReason("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={
                    expireSubmitting ||
                    expireReason.trim().length < 10
                  }
                  onClick={async () => {
                    if (accessToken === null || loadId === undefined) {return;}
                    setExpireSubmitting(true);
                    try {
                      await loadsAdminApi.forceExpire(
                        accessToken, loadId, expireReason.trim(),
                      );
                      toast.success("Load force-expired.");
                      setExpireOpen(false);
                      setExpireReason("");
                      void fetchLoad();
                    } catch (e: unknown) {
                      toast.error(
                        e instanceof ApiError
                          ? `Force-expire failed: ${e.problem.detail}`
                          : "Force-expire failed.",
                      );
                    } finally {
                      setExpireSubmitting(false);
                    }
                  }}
                >
                  {expireSubmitting ? "Expiring…" : "Confirm force-expire"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function statusClass(status: string): string {
  switch (status) {
    case "open_for_bidding":
      return "bg-info-subtle text-info-subtle-foreground";
    case "accepted":
      return "bg-success-subtle text-success-subtle-foreground";
    case "cancelled":
    case "expired":
      return "bg-danger-subtle text-danger-subtle-foreground";
    case "completed":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-muted text-foreground";
  }
}
